import { useEffect, useMemo, useState } from "react";
import { useIntl, type IntlShape } from "react-intl";
import { Link } from "react-router";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { Select } from "@/components/design/Select";
import { Skeleton, SkeletonRegion } from "@/components/design/Skeleton";
import { StatTile } from "@/components/dashboard/StatTile";
import {
  compactNumber,
  fetchAgfStreamingTrend,
  fetchAgfTvOverview,
  fetchFfaKinoOverview,
  type FfaFormRow,
  type FfaKinoOverview,
  type FfaTopFilmPoint,
} from "@/lib/tobis-market";
import { logApplicationFailure } from "@/lib/user-error";
import { tvDayStack } from "@/lib/tobis-tv-days";
import { useTobisMarketStore } from "@/stores/tobis-market-store";
import {
  AreaStat,
  BarCompare,
  LineStat,
  MultiLineStat,
  SankeyStat,
  SectionColorProvider,
  StackedDayBars,
} from "./TobisMarketCharts";
import { TobisPageShell, TobisSearch } from "./TobisPageShell";

// Markt-Dashboard: FFA-Kinomarktdaten (V0038/V0039) und AGF-Streaming-Trend
// (V0014, bis hierher im Frontend ungenutzt). Kein Titelbezug, siehe
// ingest.source.grain='market' -- anders als die Titel-/Personen-Seiten geht
// es hier um den Gesamtmarkt, nicht um einzelne Filme.

const AGGREGATE_ROW_MARKERS = ["GESAMT", "Summe"];

function isAggregateRow(label: string): boolean {
  return AGGREGATE_ROW_MARKERS.some((marker) => label.includes(marker));
}

/**
 * Bundeslaender/Staedte-Bar-Charts zeigen nur den neuesten Jahres-Snapshot
 * (ein Balkenvergleich zwischen Kategorien macht nur fuer einen Zeitpunkt
 * Sinn). Fuer die Entwicklung ueber die Zeit reichen die groessten N Regionen
 * als Small Multiples -- alle 16 Bundeslaender oder 42 Staedte gleichzeitig
 * als Liniendiagramme waeren nicht mehr lesbar.
 */
function topRegionsOverTime(rows: { year: number; region: string; besucher: number | null }[], n: number) {
  if (rows.length === 0) return [];
  const maxYear = Math.max(...rows.map((r) => r.year));
  const latest = rows.filter((r) => r.year === maxYear && r.besucher != null);
  const topRegions = [...latest].sort((a, b) => (b.besucher ?? 0) - (a.besucher ?? 0)).slice(0, n).map((r) => r.region);
  return topRegions.map((region) => ({
    region,
    points: rows.filter((r) => r.region === region).sort((a, b) => a.year - b.year),
  }));
}

/** Wie topRegionsOverTime, aber fuer Standorte statt Besucher (eigene Funktion statt Parametrisierung, um die bestehende Signatur unangetastet zu lassen). */
function topStandorteOverTime(rows: { year: number; region: string; standorte: number | null }[], n: number) {
  if (rows.length === 0) return [];
  const maxYear = Math.max(...rows.map((r) => r.year));
  const latest = rows.filter((r) => r.year === maxYear && r.standorte != null);
  const topRegions = [...latest].sort((a, b) => (b.standorte ?? 0) - (a.standorte ?? 0)).slice(0, n).map((r) => r.region);
  return topRegions.map((region) => ({
    region,
    points: rows.filter((r) => r.region === region).sort((a, b) => a.year - b.year),
  }));
}

/** Alle Sonderformen ueber die Zeit gruppiert (nur 9 Formen nach Filterung im BFF, kein Top-N noetig). */
function sonderformenOverTime(rows: { year: number; form: string; tickets: number | null }[]) {
  const forms = [...new Set(rows.map((r) => r.form))];
  return forms.map((form) => ({
    form,
    points: rows.filter((r) => r.form === form).sort((a, b) => a.year - b.year),
  }));
}

/** Vorzeichenbehaftete YoY-Veraenderung, "+2,1 %"/"-11,9 %" -- aus unserer eigenen Reihe berechnet, nicht aus dem PDF-Text uebernommen (der zeigt dieselbe Zahl nur fuer das jeweils neueste Jahr). */
function yoyChange(current: number, previous: number): string {
  const pct = ((current - previous) / previous) * 100;
  const sign = pct >= 0 ? "+" : "";
  return `${sign}${pct.toFixed(1).replace(".", ",")} %`;
}

function SectionCard({
  title,
  colorIndex,
  children,
}: {
  title: string;
  /** Index in SECTION_COLOR_PALETTE, aufsteigend pro Kachel auf der Seite vergeben. */
  colorIndex: number;
  children: React.ReactNode;
}) {
  return (
    <div className="box bg-surface p-6">
      <div className="text-[10px] tracking-[0.25em] text-ink/55">{title}</div>
      <div className="mt-4">
        <SectionColorProvider colorIndex={colorIndex}>{children}</SectionColorProvider>
      </div>
    </div>
  );
}

/**
 * Aeussere Form einer SectionCard als Platzhalter: gleiche Box, gleicher
 * Titelabstand, ohne Farbkontext (der haengt an echten Daten). `tiles` spiegelt
 * die Kennzahlen-Reihe, `chartClass` die Hoehe der Diagrammflaeche darunter.
 */
function SkeletonSection({ tiles = 0, chartClass }: { tiles?: number; chartClass: string }) {
  return (
    <div className="box bg-surface p-6">
      <Skeleton className="h-2 w-32" />
      <div className="mt-4 space-y-3">
        {tiles > 0 ? (
          <div className="grid grid-cols-2 gap-3 @sm:grid-cols-4">
            {Array.from({ length: tiles }, (_, i) => (
              <div key={i} className="box-flat bg-page p-4">
                <Skeleton className="h-2.5 w-16" />
                <Skeleton className="mt-2 h-6 w-24" />
                <Skeleton className="mt-1.5 h-2 w-20" />
              </div>
            ))}
          </div>
        ) : null}
        <Skeleton className={`w-full ${chartClass}`} />
      </div>
    </div>
  );
}

/**
 * Ladezustand der Markt-Seite. FFA, AGF-Streaming und AGF-TV werden parallel
 * geholt, brauchen zusammen aber mehrere Sekunden; vorher stand hier nichts
 * ausser dem Seitenkopf. Die drei Platzhalter spiegeln die drei SectionCards in
 * ihrer Reihenfolge, damit das Layout beim Eintreffen der Daten nicht springt.
 */
function MarketSkeleton({ label }: { label: string }) {
  return (
    <SkeletonRegion label={label} className="space-y-6">
      <SkeletonSection tiles={4} chartClass="h-52" />
      <SkeletonSection chartClass="h-52" />
      <SkeletonSection chartClass="h-64" />
    </SkeletonRegion>
  );
}

/** Liste von Jahres-Belegen (verifizierte PDF-URLs, siehe V0046). */
function SourceLinksRow({ label, links }: { label: string; links: { key: string; url: string; text: string }[] }) {
  if (links.length === 0) return null;
  return (
    <div className="mt-3">
      <div className="text-[11px] text-ink/40">{label}</div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
        {links.map((l) => (
          <a
            key={l.key}
            href={l.url}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] text-ink/55 underline underline-offset-2 hover:text-ink"
          >
            {l.text}
          </a>
        ))}
      </div>
    </div>
  );
}

/** Einzelner Beleg-Link, fuer Snapshot-Kacheln (nur ein Jahr gezeigt). */
function SourceLink({ label, url }: { label: string; url: string | null }) {
  if (!url) return null;
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="mt-2 inline-block text-[11px] text-ink/55 underline underline-offset-2 hover:text-ink"
    >
      {label}
    </a>
  );
}

function formatEur(v: number): string {
  return `${compactNumber(v)} €`;
}

const HOMEVIDEO_METRIC_LABELS: Record<string, { id: string; defaultMessage: string }> = {
  kaeufer_leiher_abo_mio: { id: "tobis.market.homevideo.metric.kaeufer", defaultMessage: "Buyers/renters/subscribers" },
  reichweite_pct: { id: "tobis.market.homevideo.metric.reichweite", defaultMessage: "Reach (vs. population)" },
  ausgaben_pro_person_eur: { id: "tobis.market.homevideo.metric.ausgaben", defaultMessage: "Spend per person" },
  intensitaet_stueck: { id: "tobis.market.homevideo.metric.intensitaet", defaultMessage: "Purchases/rentals per person" },
};

function homevideoMetricLabel(intl: IntlShape, metric: string): string {
  const entry = HOMEVIDEO_METRIC_LABELS[metric];
  return entry ? intl.formatMessage(entry) : metric;
}

function homevideoMetricFormatY(metric: string): (v: number) => string {
  if (metric === "reichweite_pct") return (v) => `${v.toFixed(0)}%`;
  if (metric === "ausgaben_pro_person_eur") return (v) => `${v.toFixed(0)} €`;
  if (metric === "kaeufer_leiher_abo_mio") return (v) => `${v.toFixed(1)}M`;
  return (v) => v.toFixed(1);
}

const KONSUMKLIMA_METRIC_LABELS: Record<string, { id: string; defaultMessage: string }> = {
  konjunkturerwartung: { id: "tobis.market.konsumklima.metric.konjunktur", defaultMessage: "Economic outlook" },
  einkommenserwartung: { id: "tobis.market.konsumklima.metric.einkommen", defaultMessage: "Income expectations" },
  anschaffungsneigung: { id: "tobis.market.konsumklima.metric.anschaffung", defaultMessage: "Willingness to buy" },
  preiserwartung: { id: "tobis.market.konsumklima.metric.preis", defaultMessage: "Price expectations" },
  sparneigung: { id: "tobis.market.konsumklima.metric.sparen", defaultMessage: "Savings propensity" },
};

function konsumklimaMetricLabel(intl: IntlShape, metric: string): string {
  const entry = KONSUMKLIMA_METRIC_LABELS[metric];
  return entry ? intl.formatMessage(entry) : metric;
}

/** Indexpunkt-Veraenderung, kein Geld-/Prozent-Wert - Vorzeichen macht die Richtung sofort sichtbar (siehe Kennzahlenbox im Quell-PDF, die genauso formatiert). */
function formatIndexPoints(v: number): string {
  return `${v >= 0 ? "+" : ""}${v.toFixed(1)}`;
}

/** Die vier ueber alle 9 Jahre durchgehend gefuellten Genres (siehe Analyse-Query in der Session), Rest als "Sonstige" gebuendelt - haelt die Monochrom-Konvention ein (docs/ive-design-principles.md: "Everything else stays monochrome"), keine 11-Farben-Palette. */
const HOMEVIDEO_GENRE_TOP_CODES = ["action", "kinder", "drama", "komoedie"] as const;

const HOMEVIDEO_GENRE_LABELS: Record<string, { id: string; defaultMessage: string }> = {
  action: { id: "tobis.market.homevideo.genre.action", defaultMessage: "Action" },
  kinder: { id: "tobis.market.homevideo.genre.kinder", defaultMessage: "Kids" },
  drama: { id: "tobis.market.homevideo.genre.drama", defaultMessage: "Drama" },
  komoedie: { id: "tobis.market.homevideo.genre.komoedie", defaultMessage: "Comedy" },
  sonstige: { id: "tobis.market.homevideo.genre.sonstige", defaultMessage: "Other" },
};

function homevideoGenreLabel(intl: IntlShape, code: string): string {
  const entry = HOMEVIDEO_GENRE_LABELS[code];
  return entry ? intl.formatMessage(entry) : code;
}

const HOMEVIDEO_SANKEY_LABELS: Record<string, string> = {
  kaufvideos: "Kaufvideos",
  leihvideos_or_tvod: "Leihvideos/TVoD",
  svod: "SVoD",
  dvd_kauf: "DVD (Kauf)",
  bluray_kauf: "Blu-ray (Kauf)",
  est: "EST",
  dvd_leih: "DVD (Leihe)",
  bluray_leih: "Blu-ray (Leihe)",
  tvod_leih: "TVoD (Leihe)",
};

function homevideoSankeyLabel(code: string): string {
  return HOMEVIDEO_SANKEY_LABELS[code] ?? code;
}

/**
 * Level4-Blaetter gehoeren nicht explizit zu einem Level2-Segment in den
 * Daten (siehe model.ffa_own_homevideo_formate) - die Namenskonvention der
 * Extraktion ist aber eindeutig: alles mit "_kauf"-Suffix oder "est" ist
 * Kaufvideos, alles mit "_leih"-Suffix ist Leihvideos/TVoD. SVoD hat keine
 * weitere Aufschluesselung.
 */
function homevideoFormatParentSegment(format: string): string | null {
  if (format.endsWith("_kauf") || format === "est") return "kaufvideos";
  if (format.endsWith("_leih")) return "leihvideos_or_tvod";
  return null;
}

const AGF_AUDIENCES = ["Z3+", "E14+", "E14-29", "E14-49", "E30-49", "E50+", "K3-13"] as const;

export function TobisMarketPage() {
  const intl = useIntl();
  // FFA/AGF/AGF-TV liegen im Store, damit ein Zurueck von einer anderen
  // Tobis-Seite die drei Marktabfragen nicht neu laedt (siehe
  // tobis-market-store, dasselbe Prinzip wie bei Titel-/Personen-Register).
  const { ffa, agf, agfTvHitliste, agfTvMarktanteil, loaded, setData, markLoaded } = useTobisMarketStore();
  const [phase, setPhase] = useState<"loading" | "ready" | "error">(loaded ? "ready" : "loading");
  const [selectedAudiences, setSelectedAudiences] = useState<string[]>(["Z3+"]);
  const [tvAudience, setTvAudience] = useState<"total" | "e1449">("total");
  // null = juengster gesammelter Tag; ein gewaehltes Datum bleibt auch beim
  // Zielgruppen-Wechsel stehen (beide Zielgruppen kommen aus demselben Lauf).
  const [tvDate, setTvDate] = useState<string | null>(null);
  const [kpiSearch, setKpiSearch] = useState("");
  const [selectedKennzahlen, setSelectedKennzahlen] = useState<string[]>([]);
  const [kpiOverlay, setKpiOverlay] = useState(false);

  useEffect(() => {
    if (loaded) return;
    let cancelled = false;
    (async () => {
      try {
        const [ffaData, agfData, agfTvData] = await Promise.all([
          fetchFfaKinoOverview(),
          fetchAgfStreamingTrend(),
          fetchAgfTvOverview(),
        ]);
        if (cancelled) return;
        setData(ffaData, agfData, agfTvData.hitliste, agfTvData.marktanteil);
        setPhase("ready");
      } catch {
        if (cancelled) return;
        markLoaded();
        logApplicationFailure("tobis.market");
        setPhase("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loaded, setData, markLoaded]);

  const yearly = useMemo(() => ffa?.kinoYearly ?? [], [ffa]);
  const latestYearly = yearly.at(-1);
  const previousYearly = yearly.at(-2);
  // Vorsichtshalber gefiltert, auch wenn model.ffa_own_kinoergebnis_year
  // Umsatz durchgehend ab 1996 liefert (anders als die alte Kollegen-Quelle,
  // die erst ab 2010 Umsatz hatte) - damit die Flaeche nicht mit einem
  // Nullwert verzerrt startet, falls ein Jahr doch mal luckenhaft ist.
  const yearlyWithRevenue = useMemo(() => yearly.filter((y) => y.umsatzEur != null), [yearly]);

  // Neueste zwei Jahre der beiden "Marktanteil deutscher Filme"-Reihen fuer
  // die YoY-Kachel. Aus ffa.kpiYears (model.ffa_own_kpi_year, V0064) statt
  // ffa.marktanteil (model.ffa_own_marktanteil, V0060) -- letztere deckt nur
  // 1995-2014 ab, die kpiYears-Reihe reicht bis zum aktuellsten Jahr.
  const marktanteilDeutschYearly = useMemo(
    () =>
      (ffa?.kpiYears ?? [])
        .filter((k) => k.kennzahl === "Marktanteil deutscher Filme" && k.value != null)
        .sort((a, b) => a.year - b.year),
    [ffa],
  );
  const latestMarktanteil = marktanteilDeutschYearly.at(-1);
  const previousMarktanteil = marktanteilDeutschYearly.at(-2);

  const latestHomevideo = ffa?.homevideoMarkt.at(-1);
  const previousHomevideo = ffa?.homevideoMarkt.at(-2);

  // Besucher (Mio.), Umsatz (Mrd. €) und Ticketpreis (€) haben zu
  // unterschiedliche Skalen fuer ein gemeinsames Chart -- indexiert auf 100
  // im ersten Jahr mit allen drei Werten macht die relative Entwicklung
  // vergleichbar, ohne eine zweite Y-Achse zu brauchen (siehe SectionCard-
  // Erklaertext).
  const indexedYearly = useMemo(() => {
    const withAll = yearly.filter((y) => y.besucher != null && y.umsatzEur != null && y.eintrittspreisEur != null);
    const base = withAll[0];
    if (!base || base.besucher == null || base.umsatzEur == null || base.eintrittspreisEur == null) return [];
    return withAll.map((y) => ({
      year: y.year,
      besucherIdx: (y.besucher! / base.besucher!) * 100,
      umsatzIdx: (y.umsatzEur! / base.umsatzEur!) * 100,
      preisIdx: (y.eintrittspreisEur! / base.eintrittspreisEur!) * 100,
    }));
  }, [yearly]);

  const bundeslaenderChart = useMemo(
    () => ffa?.bundeslaender.filter((r) => !isAggregateRow(r.region)).map((r) => ({ ...r, region: r.region })) ?? [],
    [ffa],
  );
  const standorteBundeslandChart = useMemo(
    () => ffa?.standorteBundesland.filter((r) => !isAggregateRow(r.region)) ?? [],
    [ffa],
  );
  const bundeslaenderTop = useMemo(
    () => topRegionsOverTime(ffa?.bundeslaenderYearly.filter((r) => !isAggregateRow(r.region)) ?? [], 6),
    [ffa],
  );
  const staedteTop = useMemo(() => topRegionsOverTime(ffa?.staedteYearly ?? [], 6), [ffa]);
  const standorteBundeslandTop = useMemo(
    () => topStandorteOverTime(ffa?.standorteBundeslandYearly.filter((r) => !isAggregateRow(r.region)) ?? [], 6),
    [ffa],
  );
  const sonderformenTop = useMemo(() => sonderformenOverTime(ffa?.sonderformenYearly ?? []), [ffa]);
  const formCharts = useMemo(() => {
    const clean = (rows: FfaFormRow[]) => rows.filter((r) => !isAggregateRow(r.kategorie));
    return {
      kinoform: clean(ffa?.kinoform ?? []),
      kinogroesse: clean(ffa?.kinogroesse ?? []),
      ortsgroesse: clean(ffa?.ortsgroesse ?? []),
    };
  }, [ffa]);

  // Umsatz ist erst ab 1993 verfuegbar (Besucher schon ab 1992, siehe
  // model.ffa_own_multiplexe) - gefiltert, damit die Linie nicht mit einem
  // Nullwert fuer 1992 startet.
  const multiplexeWithRevenue = useMemo(() => ffa?.multiplexe.filter((m) => m.umsatzEur != null) ?? [], [ffa]);
  // Zwei Quellen verkettet: model.ffa_own_marktanteil (V0060, 1995-2014) und
  // die "Marktanteil deutscher Filme"-Kennzahl aus model.ffa_own_kpi_year
  // (V0064, 2015-2025) - ohne das wuerde der Chart 2014 abbrechen, obwohl die
  // StatTile oben schon den aktuellsten Wert zeigt. Bei Ueberlappung gewinnt
  // die aeltere, eigens fuer diesen Zweck gebaute Quelle.
  const marktanteilChart = useMemo(() => {
    const legacy = ffa?.marktanteil.filter((m) => m.marktanteilPct != null) ?? [];
    const legacyYears = new Set(legacy.map((m) => m.year));
    const recent = marktanteilDeutschYearly
      .filter((k) => !legacyYears.has(k.year))
      .map((k) => ({ year: k.year, marktanteilPct: k.value }));
    return [...legacy, ...recent].sort((a, b) => a.year - b.year);
  }, [ffa, marktanteilDeutschYearly]);
  // Filmzahlen (Spielfilme/Dokumentarfilme/Koproduktionen) stehen nur bis 2003
  // in der Quelle -- danach wechselt sie vom Tabellen- zum Chart-only-Format
  // (siehe Migrationskommentar V0060), deshalb eigener Filter statt marktanteilChart.
  const marktanteilFilmzahlen = useMemo(
    () => ffa?.marktanteil.filter((m) => m.spielfilme != null) ?? [],
    [ffa],
  );

  // Segmente kommen flach vom BFF (year, segment, umsatzMioEur) - fuer den
  // MultiLineStat (eine Zeile pro Jahr, ein Feld pro Segment) hier pivotiert.
  // Explizit typisiert, weil TS den Object-Spread ueber ein Map-Value sonst
  // nicht als konkrete Segment-Keys inferiert (nur "year" bliebe uebrig).
  const homevideoSegmenteChart = useMemo(() => {
    const rows = ffa?.homevideoSegmente ?? [];
    const byYear = new Map<number, { kaufvideos: number | null; leihvideos_or_tvod: number | null; svod: number | null }>();
    for (const r of rows) {
      const prev = byYear.get(r.year) ?? { kaufvideos: null, leihvideos_or_tvod: null, svod: null };
      byYear.set(r.year, { ...prev, [r.segment]: r.umsatzMioEur });
    }
    return [...byYear.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([year, segs]) => ({ year, ...segs }));
  }, [ffa]);

  // Konsumentenzahlen kommen als generisches (year, metric, value)-Pivot vom
  // BFF, da nicht jedes Jahr alle vier Metriken hat - hier nach Metrik
  // gruppiert fuer vier kleine Zeitreihen-Kacheln.
  const homevideoKonsumentenByMetric = useMemo(() => {
    const rows = ffa?.homevideoKonsumenten ?? [];
    const metrics = [...new Set(rows.map((r) => r.metric))];
    return metrics.map((metric) => ({
      metric,
      points: rows.filter((r) => r.metric === metric).sort((a, b) => a.year - b.year),
    }));
  }, [ffa]);

  // Konsumklima-Kennzahlen kommen wie die Konsumentenzahlen als generisches
  // (year, metric, value)-Pivot vom BFF (nur 2016/2023/2024/2025 gefuellt,
  // siehe V0078-Migrationskommentar) - identisches Gruppierungsmuster.
  const homevideoKonsumklimaByMetric = useMemo(() => {
    const rows = ffa?.homevideoKonsumklima ?? [];
    const metrics = [...new Set(rows.map((r) => r.metric))];
    return metrics.map((metric) => ({
      metric,
      points: rows.filter((r) => r.metric === metric).sort((a, b) => a.year - b.year),
    }));
  }, [ffa]);

  // Genre-Anteile nur fuer Format "gesamt" (einzige Spalte, die in allen 9
  // Jahren vorliegt) - die vier durchgehend gefuellten Top-Genres als eigene
  // Serie, der Rest (inkl. der schwankenden Special-Interest/Musik-Aufteilung
  // je genre_scheme) summiert in "sonstige", damit jedes Jahr auf ~100% kommt.
  const homevideoGenresChart = useMemo(() => {
    const rows = (ffa?.homevideoGenres ?? []).filter((g) => g.format === "gesamt" && g.sharePct != null);
    const byYear = new Map<number, { action: number; kinder: number; drama: number; komoedie: number; sonstige: number }>();
    for (const r of rows) {
      const prev = byYear.get(r.year) ?? { action: 0, kinder: 0, drama: 0, komoedie: 0, sonstige: 0 };
      if ((HOMEVIDEO_GENRE_TOP_CODES as readonly string[]).includes(r.genreCode)) {
        byYear.set(r.year, { ...prev, [r.genreCode]: r.sharePct! });
      } else {
        byYear.set(r.year, { ...prev, sonstige: prev.sonstige + r.sharePct! });
      }
    }
    return [...byYear.entries()].sort((a, b) => a[0] - b[0]).map(([year, g]) => ({ year, ...g }));
  }, [ffa]);

/**
   * Baut den Sankey-Baum (Gesamt -> Segmente -> Level4-Formate) fuer ein
   * einzelnes Jahr. Level3 ("dvd_blu_kauf", "est_tvod_combined") wird bewusst
   * nicht genutzt: "est_tvod_combined" ist keine echte Zwischensumme im Baum,
   * sondern eine separate Cross-Segment-Metrik quer ueber Kaufvideos UND
   * TVoD (Session-Notiz 2026-08-07) - die einzelnen Level4-Blaetter reichen
   * fuer eine korrekte, additive Struktur.
   */
  function buildHomevideoSankey(ffaData: FfaKinoOverview | null, year: number) {
    const leaves = (ffaData?.homevideoFormate ?? []).filter(
      (f) => f.year === year && f.level === "level4" && f.umsatzMioEur != null,
    );
    const segments = (ffaData?.homevideoSegmente ?? []).filter((s) => s.year === year && s.umsatzMioEur != null);

    const nodeNames = ["gesamt", ...segments.map((s) => s.segment), ...leaves.map((l) => l.format)];
    const nodeIndex = new Map(nodeNames.map((name, i) => [name, i]));
    const nodes = [
      { name: "Home Video" },
      ...segments.map((s) => ({ name: homevideoSankeyLabel(s.segment) })),
      ...leaves.map((l) => ({ name: homevideoSankeyLabel(l.format) })),
    ];

    const links = [
      ...segments.map((s) => ({
        source: nodeIndex.get("gesamt")!,
        target: nodeIndex.get(s.segment)!,
        value: s.umsatzMioEur!,
      })),
      ...leaves
        .map((l) => {
          const parent = homevideoFormatParentSegment(l.format);
          if (!parent || !nodeIndex.has(parent)) return null;
          return { source: nodeIndex.get(parent)!, target: nodeIndex.get(l.format)!, value: l.umsatzMioEur! };
        })
        .filter((l): l is { source: number; target: number; value: number } => l != null),
    ];

    return { nodes, links };
  }

  /**
   * Zwei Sankeys statt einem: das Jahr mit der meisten Detailtiefe (2016,
   * inkl. Leihvideos-Aufschluesselung) UND das aktuellste Jahr mit
   * verfuegbarer Formatdaten - ab 2023 ist "Leihvideos DVD/Blu-ray" praktisch
   * verschwunden (TVoD wird zum eigenstaendigen Segment), dadurch weniger
   * Detail, aber aktueller.
   */
  const homevideoSankeys = useMemo(() => {
    const formate = ffa?.homevideoFormate ?? [];
    const level4CountByYear = new Map<number, number>();
    for (const f of formate) {
      if (f.level !== "level4" || f.umsatzMioEur == null) continue;
      level4CountByYear.set(f.year, (level4CountByYear.get(f.year) ?? 0) + 1);
    }
    if (level4CountByYear.size === 0) return [];

    let mostDetailedYear = 0;
    let bestCount = 0;
    for (const [year, count] of level4CountByYear) {
      if (count > bestCount) {
        mostDetailedYear = year;
        bestCount = count;
      }
    }
    const latestYear = Math.max(...level4CountByYear.keys());

    const years = latestYear === mostDetailedYear ? [mostDetailedYear] : [mostDetailedYear, latestYear];
    return years.map((year) => ({ year, data: buildHomevideoSankey(ffa, year) }));
  }, [ffa]);

  // Top-Filme je Jahr fuer den Tooltip der Jahresreihen ("wo kommt diese Zahl
  // ungefaehr her") - zwei getrennte Karten, weil die Marktanteil-Kachel nur
  // deutsche Produktionen zeigen soll, Besucher/Kinoumsatz aber den Gesamtmarkt.
  const byYear = (rows: FfaTopFilmPoint[]) => {
    const map = new Map<number, FfaTopFilmPoint[]>();
    for (const f of rows) map.set(f.year, [...(map.get(f.year) ?? []), f]);
    return map;
  };
  const topFilmsByYear = useMemo(() => byYear(ffa?.topFilmsByYear ?? []), [ffa]);
  const topGermanFilmsByYear = useMemo(() => byYear(ffa?.topGermanFilmsByYear ?? []), [ffa]);

  const topFilmsTooltip = (map: Map<number, FfaTopFilmPoint[]>) => (label: unknown) => {
    const films = map.get(Number(label));
    if (!films?.length) return null;
    return (
      <div className="mt-1.5 max-w-[240px] border-t border-ink/10 pt-1.5">
        <div className="text-[10px] uppercase tracking-[0.15em] text-ink/40">
          {intl.formatMessage({ id: "tobis.market.topFilms", defaultMessage: "Top films" })}
        </div>
        {films.map((f) => (
          <Link
            key={f.imdbId}
            to={`/tobis/titles/${f.tvmazeId}`}
            className="mt-1 flex items-center gap-2 hover:bg-ink/[0.04]"
          >
            <div className="aspect-[2/3] h-8 shrink-0 overflow-hidden rounded bg-ink/[0.06]">
              {f.coverUrl ? <img src={f.coverUrl} alt="" className="h-full w-full object-cover" /> : null}
            </div>
            <div className="truncate text-ink/70">
              {f.name ?? f.imdbId}
              {f.admissions != null ? <span className="text-ink/40"> · {compactNumber(f.admissions)}</span> : null}
            </div>
          </Link>
        ))}
      </div>
    );
  };

  // Neun bereinigte Kennzahlen aus model.ffa_own_kpi_year (V0061, selbst aus
  // den FFA-Original-PDFs extrahiert) -- vorher liefen hier 78 Fussnoten-
  // Varianten derselben Sache aus der Kollegen-Quelle model.ffa_kpi_year
  // ("Durchschn. Eintrittspreis3" vs. "durchschn. Eintrittspreis in €"). Die
  // Pill-Liste bleibt trotzdem sinnvoll (Mehrfachauswahl zum Vergleichen),
  // ist jetzt aber deutlich uebersichtlicher.
  const allKennzahlen = useMemo(
    () => (ffa ? [...new Set(ffa.kpiYears.map((k) => k.kennzahl))].sort((a, b) => a.localeCompare(b)) : []),
    [ffa],
  );
  const visibleKennzahlen = useMemo(() => {
    const q = kpiSearch.trim().toLowerCase();
    return q ? allKennzahlen.filter((k) => k.toLowerCase().includes(q)) : allKennzahlen;
  }, [allKennzahlen, kpiSearch]);

  // Grobe thematische Einteilung nach Schluesselwoertern im rohen Namen,
  // damit die Pill-Liste nicht eine einzige lange Wand ist. Reihenfolge der
  // Checks ist die Prioritaet: "Tickets pro Einwohner" ist eine
  // Besucherkennzahl, keine Bevoelkerungskennzahl, auch wenn "Einwohner"
  // drinsteht -- deshalb Ticket/Umsatz/Marktanteil vor Einwohner pruefen.
  const visibleGroups = useMemo(() => {
    const GROUP_ADMISSIONS = intl.formatMessage({
      id: "tobis.market.kpiExplorer.group.admissions",
      defaultMessage: "Admissions / tickets",
    });
    const GROUP_REVENUE = intl.formatMessage({
      id: "tobis.market.kpiExplorer.group.revenue",
      defaultMessage: "Box office / ticket price",
    });
    const GROUP_MARKET_SHARE = intl.formatMessage({
      id: "tobis.market.kpiExplorer.group.marketShare",
      defaultMessage: "Market share",
    });
    const GROUP_POPULATION = intl.formatMessage({
      id: "tobis.market.kpiExplorer.group.population",
      defaultMessage: "Population",
    });
    const GROUP_INFRASTRUCTURE = intl.formatMessage({
      id: "tobis.market.kpiExplorer.group.infrastructure",
      defaultMessage: "Venues, screens & seats",
    });
    const GROUP_ORDER = [GROUP_ADMISSIONS, GROUP_REVENUE, GROUP_MARKET_SHARE, GROUP_POPULATION, GROUP_INFRASTRUCTURE];
    const categoryOf = (k: string): string => {
      const s = k.toLowerCase();
      if (s.includes("marktanteil")) return GROUP_MARKET_SHARE;
      if (s.includes("ticket") || s.includes("besuch")) return GROUP_ADMISSIONS;
      if (s.includes("umsatz") || s.includes("eintrittspreis")) return GROUP_REVENUE;
      if (s.includes("einwohner")) return GROUP_POPULATION;
      return GROUP_INFRASTRUCTURE;
    };
    const groups = new Map<string, string[]>();
    for (const k of visibleKennzahlen) {
      const cat = categoryOf(k);
      groups.set(cat, [...(groups.get(cat) ?? []), k]);
    }
    return GROUP_ORDER.filter((g) => groups.has(g)).map((g) => [g, groups.get(g)!] as const);
  }, [visibleKennzahlen, intl]);

  function toggleKennzahl(kennzahl: string) {
    setSelectedKennzahlen((prev) =>
      prev.includes(kennzahl) ? prev.filter((k) => k !== kennzahl) : [...prev, kennzahl],
    );
  }

  const kpiMatches = useMemo(() => {
    if (!ffa) return [];
    return selectedKennzahlen.map((kennzahl) => {
      const points = ffa.kpiYears.filter((k) => k.kennzahl === kennzahl).sort((a, b) => a.year - b.year);
      return { kennzahl, points, sourceTable: points[0]?.sourceTable ?? "" };
    });
  }, [ffa, selectedKennzahlen]);
  const kpiKennzahlCount = allKennzahlen.length;

  // Ueberlagerte Ansicht fuer beliebig gewaehlte Kennzahlen, dieselbe Technik
  // wie die feste "Besucher/Kinoumsatz/Eintrittspreis, indexiert"-Kachel oben
  // (indexedYearly): rohe Werte sind nicht vergleichbar (Besucher pro
  // Sitzplatz ~100-220 gegen Kinoumsatz in Mrd. €), auf 100 im ersten Jahr
  // MIT ALLEN gewaehlten Werten indexiert schon. Anders als bei der festen
  // Kachel kann das Basisjahr je nach Auswahl variieren (z.B. "Marktanteil
  // deutscher Filme" beginnt erst 2015, siehe V0064) - deshalb hier bestimmt
  // statt hart auf das erste Jahr der Reihe.
  const kpiOverlayData = useMemo(() => {
    if (kpiMatches.length < 2) return [];
    const byKennzahl = new Map(kpiMatches.map((m) => [m.kennzahl, new Map(m.points.map((p) => [p.year, p.value]))]));
    const allYears = new Set<number>();
    for (const m of kpiMatches) for (const p of m.points) allYears.add(p.year);
    const years = [...allYears].sort((a, b) => a - b);
    const baseYear = years.find((y) => kpiMatches.every((m) => byKennzahl.get(m.kennzahl)?.get(y) != null));
    if (baseYear === undefined) return [];
    const bases = new Map(kpiMatches.map((m) => [m.kennzahl, byKennzahl.get(m.kennzahl)!.get(baseYear)!]));
    return years
      .filter((y) => y >= baseYear)
      .map((y) => {
        const row: Record<string, number | null> = { year: y };
        for (const m of kpiMatches) {
          const raw = byKennzahl.get(m.kennzahl)?.get(y) ?? null;
          row[m.kennzahl] = raw != null ? (raw / bases.get(m.kennzahl)!) * 100 : null;
        }
        return row;
      });
  }, [kpiMatches]);

  const staedteChart = useMemo(() => ffa?.staedte.slice(0, 15) ?? [], [ffa]);

  const agfSources = useMemo(() => {
    if (!agf) return [];
    const seen = new Map<string, string>();
    for (const p of agf) {
      if (p.sourceUrl && !seen.has(p.sourceUrl)) seen.set(p.sourceUrl, p.month);
    }
    return [...seen.entries()].sort((a, b) => b[1].localeCompare(a[1]));
  }, [agf]);

  // Mehrere Zielgruppen additiv waehlbar (Marvin, 2026-08-07) - pro Plattform
  // eine Zeile je Monat, eine Spalte je ausgewaehlter Zielgruppe, fuer
  // MultiLineStat (Strichmuster statt Farbe fuer die Zielgruppen-Identitaet,
  // wie beim Home-Video-Segmente-Chart).
  const agfByPlatform = useMemo(() => {
    if (!agf) return [];
    const filtered = agf.filter((p) => selectedAudiences.includes(p.audience));
    const platforms = [...new Set(filtered.map((p) => p.platform))].sort();
    return platforms.map((platform) => {
      const rows = filtered.filter((p) => p.platform === platform);
      const byMonth = new Map<string, Record<string, number | null>>();
      for (const r of rows) {
        byMonth.set(r.month, { ...(byMonth.get(r.month) ?? {}), [r.audience]: r.value });
      }
      const points = [...byMonth.entries()]
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([month, values]) => ({ month, ...values })) as ({ month: string } & Record<string, number | null>)[];
      return { platform, points };
    });
  }, [agf, selectedAudiences]);

  // Beide Kennzahlen teilen sich denselben Zielgruppen-Umschalter: fachlich
  // gehoeren Tages-Hitliste und Monatsmarktanteile zur selben Frage ("wie
  // performt das Fernsehen bei dieser Zielgruppe"), nicht zwei unabhaengige.
  // Alle gesammelten Snapshot-Tage (BFF liefert die letzten 60 Kalendertage),
  // juengster zuerst - die Luecken dazwischen sind echt, der Scheduler laeuft
  // nicht taeglich und die Quellseite fuehrt kein Archiv.
  const tvDates = useMemo(
    () => [...new Set(agfTvHitliste.map((r) => r.date))].sort((a, b) => b.localeCompare(a)),
    [agfTvHitliste],
  );
  const effectiveTvDate = tvDate ?? tvDates[0] ?? null;
  // Tagesbalken nach Sender: die einzige Aufteilung, die ueber Tage hinweg
  // vergleichbar ist, weil die Sendungen taeglich wechseln und die Sender
  // nicht (Marvin, 2026-08-17).
  const tvStack = useMemo(() => tvDayStack(agfTvHitliste, tvAudience), [agfTvHitliste, tvAudience]);

  const tvHitlisteRows = useMemo(
    () =>
      agfTvHitliste
        .filter((r) => r.audience === tvAudience && r.date === effectiveTvDate)
        .sort((a, b) => a.rang - b.rang),
    [agfTvHitliste, tvAudience, effectiveTvDate],
  );
  const tvMarktanteilRows = useMemo(
    () => agfTvMarktanteil.filter((r) => r.audience === tvAudience).sort((a, b) => a.rang - b.rang),
    [agfTvMarktanteil, tvAudience],
  );
  const tvMarktanteilChart = useMemo(
    () => tvMarktanteilRows.slice(0, 15).map((r) => ({ sender: r.sender, anteilPct: r.anteilPct })),
    [tvMarktanteilRows],
  );

  return (
    <TobisPageShell
      eyebrow={intl.formatMessage({ id: "tobis.eyebrow", defaultMessage: "Movie" })}
      title={intl.formatMessage({ id: "tobis.market.title", defaultMessage: "Market" })}
      intro={intl.formatMessage({
        id: "tobis.market.intro",
        defaultMessage:
          "German cinema and streaming market, not tied to a single title — the ground the valuation numbers stand on.",
      })}
    >
      {phase === "loading" && (
        <MarketSkeleton
          label={intl.formatMessage({ id: "tobis.market.loading", defaultMessage: "Loading market data …" })}
        />
      )}

      {phase === "error" && (
        <div className="box bg-surface p-6">
          <p className="max-w-xl text-sm text-ink/55">
            {intl.formatMessage({ id: "tobis.market.loadError", defaultMessage: "The market overview could not be loaded." })}
          </p>
        </div>
      )}

      {phase === "ready" && ffa && (
        <>
          <SectionCard title={intl.formatMessage({ id: "tobis.market.cinema.title", defaultMessage: "FFA — cinema" })} colorIndex={0}>
            <div className="grid grid-cols-2 gap-3 @sm:grid-cols-4">
              <StatTile
                compact
                label={intl.formatMessage({ id: "tobis.market.admissions", defaultMessage: "Admissions" })}
                value={
                  latestYearly?.besucher != null
                    ? `${compactNumber(latestYearly.besucher)} (${latestYearly.year})`
                    : "—"
                }
                sub={
                  latestYearly?.besucher != null && previousYearly?.besucher != null
                    ? intl.formatMessage(
                        { id: "tobis.market.yoy", defaultMessage: "{change} vs. {year}" },
                        { change: yoyChange(latestYearly.besucher, previousYearly.besucher), year: previousYearly.year },
                      )
                    : undefined
                }
              />
              <StatTile
                compact
                label={intl.formatMessage({ id: "tobis.market.revenue", defaultMessage: "Box office" })}
                value={yearlyWithRevenue.at(-1)?.umsatzEur != null ? formatEur(yearlyWithRevenue.at(-1)!.umsatzEur!) : "—"}
                sub={
                  yearlyWithRevenue.at(-1)?.umsatzEur != null && yearlyWithRevenue.at(-2)?.umsatzEur != null
                    ? intl.formatMessage(
                        { id: "tobis.market.yoy", defaultMessage: "{change} vs. {year}" },
                        {
                          change: yoyChange(yearlyWithRevenue.at(-1)!.umsatzEur!, yearlyWithRevenue.at(-2)!.umsatzEur!),
                          year: yearlyWithRevenue.at(-2)!.year,
                        },
                      )
                    : undefined
                }
              />
              {latestMarktanteil?.value != null && (
                <StatTile
                  compact
                  label={intl.formatMessage(
                    { id: "tobis.market.marketShareLatest", defaultMessage: "German market share ({year})" },
                    { year: latestMarktanteil.year },
                  )}
                  value={`${latestMarktanteil.value.toFixed(1)} %`}
                  sub={
                    previousMarktanteil?.value != null
                      ? intl.formatMessage(
                          { id: "tobis.market.yoyPoints", defaultMessage: "{change} PP vs. {year}" },
                          {
                            change: (() => {
                              const diff = latestMarktanteil.value - previousMarktanteil.value;
                              return `${diff >= 0 ? "+" : ""}${diff.toFixed(1).replace(".", ",")}`;
                            })(),
                            year: previousMarktanteil.year,
                          },
                        )
                      : undefined
                  }
                />
              )}
              <StatTile
                compact
                label={intl.formatMessage(
                  { id: "tobis.market.homevideoLatest", defaultMessage: "Home video ({year})" },
                  { year: latestHomevideo?.year ?? "–" },
                )}
                value={latestHomevideo?.umsatzMioEur != null ? `${compactNumber(latestHomevideo.umsatzMioEur)}M €` : "—"}
                sub={
                  latestHomevideo?.umsatzMioEur != null && previousHomevideo?.umsatzMioEur != null
                    ? intl.formatMessage(
                        { id: "tobis.market.yoy", defaultMessage: "{change} vs. {year}" },
                        {
                          change: yoyChange(latestHomevideo.umsatzMioEur, previousHomevideo.umsatzMioEur),
                          year: previousHomevideo.year,
                        },
                      )
                    : undefined
                }
              />
            </div>

            <div className="mt-6 grid gap-6 @lg:grid-cols-2">
              <div>
                <div className="mb-2 text-[12px] text-ink/55">
                  {intl.formatMessage(
                    { id: "tobis.market.admissionsByYear", defaultMessage: "Admissions per year, {from}–{to}" },
                    { from: yearly[0]?.year ?? "–", to: yearly.at(-1)?.year ?? "–" },
                  )}
                </div>
                <LineStat
                  data={yearly}
                  xKey="year"
                  yKey="besucher"
                  valueLabel={intl.formatMessage({ id: "tobis.market.admissions", defaultMessage: "Admissions" })}
                  tooltipExtra={topFilmsTooltip(topFilmsByYear)}
                />
              </div>
              <div>
                <div className="mb-2 text-[12px] text-ink/55">
                  {intl.formatMessage(
                    { id: "tobis.market.revenueByYear", defaultMessage: "Box office per year, {from}–{to}" },
                    { from: yearlyWithRevenue[0]?.year ?? "–", to: yearlyWithRevenue.at(-1)?.year ?? "–" },
                  )}
                </div>
                <AreaStat
                  data={yearlyWithRevenue}
                  xKey="year"
                  yKey="umsatzEur"
                  formatY={formatEur}
                  valueLabel={intl.formatMessage({ id: "tobis.market.revenue", defaultMessage: "Box office" })}
                  tooltipExtra={topFilmsTooltip(topFilmsByYear)}
                />
              </div>
            </div>

            <SourceLinksRow
              label={intl.formatMessage({ id: "tobis.market.sources.kinoergebnisse", defaultMessage: "Source documents:" })}
              links={ffa.sources.kinoergebnisse.map((s) => ({ key: String(s.year), url: s.url, text: String(s.year) }))}
            />
          </SectionCard>

          <SectionCard
            title={intl.formatMessage(
              { id: "tobis.market.monthly.title", defaultMessage: "Admissions by month, {from}–{to}" },
              { from: ffa.kinoMonthly[0]?.yearMonth.slice(0, 4) ?? "–", to: ffa.kinoMonthly.at(-1)?.yearMonth.slice(0, 4) ?? "–" },
            )}
            colorIndex={1}
          >
            <LineStat
              data={ffa.kinoMonthly}
              xKey="yearMonth"
              yKey="besucher"
              height={220}
              valueLabel={intl.formatMessage({ id: "tobis.market.admissions", defaultMessage: "Admissions" })}
            />
            <SourceLinksRow
              label={intl.formatMessage({ id: "tobis.market.sources.kinoergebnisse", defaultMessage: "Source documents:" })}
              links={ffa.sources.kinojahrMonatlich.map((s) => ({ key: String(s.year), url: s.url, text: String(s.year) }))}
            />
          </SectionCard>

          <SectionCard
            title={intl.formatMessage({
              id: "tobis.market.correlation.title",
              defaultMessage: "Admissions, box office & ticket price, indexed",
            })}
            colorIndex={2}
          >
            <p className="mb-3 max-w-2xl text-[12px] text-ink/55">
              {intl.formatMessage({
                id: "tobis.market.correlation.note",
                defaultMessage:
                  "Admissions (millions), box office (€) and ticket price (€) have very different scales: a dual-axis chart would distort how they actually move relative to each other. Indexed to 100 in {baseYear} instead, same starting point, comparable growth.",
              }, { baseYear: indexedYearly[0]?.year ?? "–" })}
            </p>
            <MultiLineStat
              data={indexedYearly}
              xKey="year"
              formatY={(v) => v.toFixed(0)}
              height={260}
              series={[
                { key: "besucherIdx", label: intl.formatMessage({ id: "tobis.market.admissions", defaultMessage: "Admissions" }) },
                { key: "umsatzIdx", label: intl.formatMessage({ id: "tobis.market.revenue", defaultMessage: "Box office" }) },
                { key: "preisIdx", label: intl.formatMessage({ id: "tobis.market.ticketPrice", defaultMessage: "Ticket price" }) },
              ]}
            />
          </SectionCard>

          <SectionCard
            title={intl.formatMessage({ id: "tobis.market.infrastructure.title", defaultMessage: "FFA cinema infrastructure over time" })}
            colorIndex={3}
          >
            <p className="mb-3 max-w-2xl text-[12px] text-ink/40">
              {intl.formatMessage({
                id: "tobis.market.infrastructure.methodBreak2025",
                defaultMessage:
                  "2025 is not directly comparable to prior years for venues, screens and seats: the source PDF changed how the cinema stock is counted (excludes drive-ins, open airs, film festivals and one-off venues from 2025 on). Ticket price is unaffected.",
              })}
            </p>
            <div className="grid gap-6 @lg:grid-cols-2">
              <div>
                <div className="mb-2 text-[12px] text-ink/55">
                  {intl.formatMessage({ id: "tobis.market.venuesByYear", defaultMessage: "Venues per year" })}
                </div>
                <LineStat
                  data={yearly}
                  xKey="year"
                  yKey="standorte"
                  valueLabel={intl.formatMessage({ id: "tobis.market.venues", defaultMessage: "Venues" })}
                />
              </div>
              <div>
                <div className="mb-2 text-[12px] text-ink/55">
                  {intl.formatMessage({ id: "tobis.market.screensByYear", defaultMessage: "Screens per year" })}
                </div>
                <LineStat
                  data={yearly}
                  xKey="year"
                  yKey="leinwaende"
                  valueLabel={intl.formatMessage({ id: "tobis.market.screens", defaultMessage: "Screens" })}
                />
              </div>
              <div>
                <div className="mb-2 text-[12px] text-ink/55">
                  {intl.formatMessage({ id: "tobis.market.seatsByYear", defaultMessage: "Seats per year" })}
                </div>
                <LineStat
                  data={yearly}
                  xKey="year"
                  yKey="sitzplaetze"
                  valueLabel={intl.formatMessage({ id: "tobis.market.seats", defaultMessage: "Seats" })}
                />
              </div>
              <div>
                <div className="mb-2 text-[12px] text-ink/55">
                  {intl.formatMessage({ id: "tobis.market.ticketPriceByYear", defaultMessage: "Average ticket price per year" })}
                </div>
                <LineStat
                  data={yearly}
                  xKey="year"
                  yKey="eintrittspreisEur"
                  formatY={(v) => `${v.toFixed(2)} €`}
                  valueLabel={intl.formatMessage({ id: "tobis.market.ticketPrice", defaultMessage: "Ticket price" })}
                />
              </div>
            </div>
            <SourceLinksRow
              label={intl.formatMessage({ id: "tobis.market.sources.kinoergebnisse", defaultMessage: "Source documents:" })}
              links={ffa.sources.kinoergebnisse.map((s) => ({ key: String(s.year), url: s.url, text: String(s.year) }))}
            />
          </SectionCard>

          <SectionCard
            title={intl.formatMessage({ id: "tobis.market.marketShare.title", defaultMessage: "German film market share" })}
            colorIndex={4}
          >
            <LineStat
              data={marktanteilChart}
              xKey="year"
              yKey="marktanteilPct"
              formatY={(v) => `${v.toFixed(1)}%`}
              valueLabel={intl.formatMessage({ id: "tobis.market.marketShare", defaultMessage: "German market share" })}
              tooltipExtra={topFilmsTooltip(topGermanFilmsByYear)}
            />

            {marktanteilFilmzahlen.length > 0 && (
              <>
                <div className="mt-6 text-[12px] text-ink/55">
                  {intl.formatMessage(
                    { id: "tobis.market.germanFilmCounts", defaultMessage: "German film releases by category, {from}–{to}" },
                    { from: marktanteilFilmzahlen[0]?.year ?? "–", to: marktanteilFilmzahlen.at(-1)?.year ?? "–" },
                  )}
                </div>
                <MultiLineStat
                  data={marktanteilFilmzahlen}
                  xKey="year"
                  formatY={(v) => v.toFixed(0)}
                  height={180}
                  series={[
                    { key: "spielfilme", label: intl.formatMessage({ id: "tobis.market.spielfilme", defaultMessage: "Feature films" }) },
                    { key: "dokumentarfilme", label: intl.formatMessage({ id: "tobis.market.dokumentarfilme", defaultMessage: "Documentaries" }) },
                    { key: "koproduktionen", label: intl.formatMessage({ id: "tobis.market.koproduktionen", defaultMessage: "Co-productions" }) },
                  ]}
                />
              </>
            )}
            <SourceLinksRow
              label={intl.formatMessage({ id: "tobis.market.sources.kinoergebnisse", defaultMessage: "Source documents:" })}
              links={ffa.sources.marktanteilAlt.map((s) => ({ key: String(s.year), url: s.url, text: String(s.year) }))}
            />
            <SourceLink
              label={intl.formatMessage({ id: "tobis.market.sources.document2025", defaultMessage: "Source document, 2021–2025" })}
              url={ffa.sources.marktanteilNeuUrl}
            />
          </SectionCard>

          <SectionCard
            title={intl.formatMessage({ id: "tobis.market.multiplex.title", defaultMessage: "Multiplex segment over time" })}
            colorIndex={5}
          >
            <div className="grid gap-6 @lg:grid-cols-2">
              <div>
                <div className="mb-2 text-[12px] text-ink/55">
                  {intl.formatMessage(
                    { id: "tobis.market.admissionsByYear", defaultMessage: "Admissions per year, {from}–{to}" },
                    { from: ffa.multiplexe[0]?.year ?? "–", to: ffa.multiplexe.at(-1)?.year ?? "–" },
                  )}
                </div>
                <LineStat
                  data={ffa.multiplexe}
                  xKey="year"
                  yKey="besucher"
                  valueLabel={intl.formatMessage({ id: "tobis.market.admissions", defaultMessage: "Admissions" })}
                />
              </div>
              <div>
                <div className="mb-2 text-[12px] text-ink/55">
                  {intl.formatMessage(
                    { id: "tobis.market.revenueByYear", defaultMessage: "Box office per year, {from}–{to}" },
                    { from: multiplexeWithRevenue[0]?.year ?? "–", to: multiplexeWithRevenue.at(-1)?.year ?? "–" },
                  )}
                </div>
                <AreaStat
                  data={multiplexeWithRevenue}
                  xKey="year"
                  yKey="umsatzEur"
                  formatY={formatEur}
                  valueLabel={intl.formatMessage({ id: "tobis.market.revenue", defaultMessage: "Box office" })}
                />
              </div>
            </div>
            <SourceLinksRow
              label={intl.formatMessage({ id: "tobis.market.sources.kinoergebnisse", defaultMessage: "Source documents:" })}
              links={ffa.sources.multiplexe.map((s) => ({ key: String(s.year), url: s.url, text: String(s.year) }))}
            />
          </SectionCard>

          <SectionCard
            title={intl.formatMessage(
              { id: "tobis.market.states.title", defaultMessage: "Admissions by state, Germany {year}" },
              { year: ffa.bundeslaenderLatestYear ?? "–" },
            )}
            colorIndex={6}
          >
            <div className="grid gap-6 @lg:grid-cols-2">
              <div>
                <div className="mb-2 text-[12px] text-ink/55">
                  {intl.formatMessage({ id: "tobis.market.admissions", defaultMessage: "Admissions" })}
                </div>
                <BarCompare
                  data={bundeslaenderChart}
                  xKey="region"
                  yKey="besucher"
                  valueLabel={intl.formatMessage({ id: "tobis.market.admissions", defaultMessage: "Admissions" })}
                  height={280}
                />
              </div>
              <div>
                <div className="mb-2 text-[12px] text-ink/55">
                  {intl.formatMessage(
                    { id: "tobis.market.venuesByState.title", defaultMessage: "Venues by state, Germany {year}" },
                    { year: ffa.standorteBundeslandLatestYear ?? "–" },
                  )}
                </div>
                <BarCompare
                  data={standorteBundeslandChart}
                  xKey="region"
                  yKey="standorte"
                  valueLabel={intl.formatMessage({ id: "tobis.market.venues", defaultMessage: "Venues" })}
                  height={280}
                />
              </div>
            </div>

            {/* spielstaetten/leinwaende kommen aus derselben Abfrage wie
                standorte (standorteBundeslandChart) - bisher ungenutzt in der
                Bundesland-Ansicht, obwohl schon geladen. */}
            <div className="mt-6 grid gap-6 @lg:grid-cols-2">
              <div>
                <div className="mb-2 text-[12px] text-ink/55">
                  {intl.formatMessage({ id: "tobis.market.screeningVenues", defaultMessage: "Screening venues" })}
                </div>
                <BarCompare
                  data={standorteBundeslandChart}
                  xKey="region"
                  yKey="spielstaetten"
                  valueLabel={intl.formatMessage({ id: "tobis.market.screeningVenues", defaultMessage: "Screening venues" })}
                  height={280}
                />
              </div>
              <div>
                <div className="mb-2 text-[12px] text-ink/55">
                  {intl.formatMessage({ id: "tobis.market.screens", defaultMessage: "Screens" })}
                </div>
                <BarCompare
                  data={standorteBundeslandChart}
                  xKey="region"
                  yKey="leinwaende"
                  valueLabel={intl.formatMessage({ id: "tobis.market.screens", defaultMessage: "Screens" })}
                  height={280}
                />
              </div>
            </div>
            <SourceLinksRow
              label={intl.formatMessage({ id: "tobis.market.sources.kinoergebnisse", defaultMessage: "Source documents:" })}
              links={ffa.sources.standorteBundesland.map((s) => ({ key: String(s.year), url: s.url, text: String(s.year) }))}
            />

            {/* eintrittspreisEur ist Teil von bundeslaenderChart (FfaRegionRow),
                bisher nur besucher daraus gezeigt - die eigentliche Story
                hinter "Ticketanteil vs. Umsatzanteil" (Marvin, 2026-08-07):
                Umsatz ist strukturell fast Tickets*Preis, ein Korrelations-
                Scatter waere nahe einer Geraden. Der Preis selbst schwankt
                dagegen wirklich zwischen Bundeslaendern (Hamburg teuerste,
                Saarland guenstigste Laender in der Regel). */}
            <div className="mt-6">
              <div className="mb-2 text-[12px] text-ink/55">
                {intl.formatMessage(
                  { id: "tobis.market.ticketPriceByState.title", defaultMessage: "Average ticket price by state, Germany {year}" },
                  { year: ffa.bundeslaenderLatestYear ?? "–" },
                )}
              </div>
              <BarCompare
                data={bundeslaenderChart}
                xKey="region"
                yKey="eintrittspreisEur"
                formatY={(v) => `${v.toFixed(2)} €`}
                valueLabel={intl.formatMessage({ id: "tobis.market.ticketPrice", defaultMessage: "Ticket price" })}
                height={280}
              />
            </div>

            <SourceLinksRow
              label={intl.formatMessage({ id: "tobis.market.sources.kinoergebnisse", defaultMessage: "Source documents:" })}
              links={ffa.sources.bundeslaenderHistorisch.map((s) => ({ key: String(s.year), url: s.url, text: String(s.year) }))}
            />

            <div className="mt-6 text-[12px] text-ink/55">
              {intl.formatMessage(
                { id: "tobis.market.topOverTime", defaultMessage: "Top 6 over time" },
                { metric: intl.formatMessage({ id: "tobis.market.admissions", defaultMessage: "Admissions" }) },
              )}
            </div>
            <div className="mt-2 grid gap-4 @sm:grid-cols-2 @lg:grid-cols-3">
              {bundeslaenderTop.map(({ region, points }) => (
                <div key={region}>
                  <div className="mb-1 text-[11px] text-ink/55">{region}</div>
                  <LineStat data={points} xKey="year" yKey="besucher" height={120} valueLabel={region} />
                </div>
              ))}
            </div>

            <div className="mt-6 text-[12px] text-ink/55">
              {intl.formatMessage(
                { id: "tobis.market.topOverTime", defaultMessage: "Top 6 over time" },
                { metric: intl.formatMessage({ id: "tobis.market.venues", defaultMessage: "Venues" }) },
              )}
            </div>
            <div className="mt-2 grid gap-4 @sm:grid-cols-2 @lg:grid-cols-3">
              {standorteBundeslandTop.map(({ region, points }) => (
                <div key={region}>
                  <div className="mb-1 text-[11px] text-ink/55">{region}</div>
                  <LineStat data={points} xKey="year" yKey="standorte" height={120} valueLabel={region} />
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard
            title={intl.formatMessage(
              { id: "tobis.market.cities.title", defaultMessage: "Top cities by tickets, Germany {year}" },
              { year: ffa.staedteLatestYear ?? "–" },
            )}
            colorIndex={7}
          >
            <BarCompare
              data={staedteChart}
              xKey="stadt"
              yKey="tickets"
              valueLabel={intl.formatMessage({ id: "tobis.market.tickets", defaultMessage: "Tickets" })}
              height={320}
            />
            <SourceLinksRow
              label={intl.formatMessage({ id: "tobis.market.sources.kinoergebnisse", defaultMessage: "Source documents:" })}
              links={ffa.sources.staedteHistorisch.map((s) => ({ key: String(s.year), url: s.url, text: String(s.year) }))}
            />

            <div className="mt-6 text-[12px] text-ink/55">
              {intl.formatMessage(
                { id: "tobis.market.topOverTime", defaultMessage: "Top 6 over time" },
                { metric: intl.formatMessage({ id: "tobis.market.tickets", defaultMessage: "Tickets" }) },
              )}
            </div>
            <div className="mt-2 grid gap-4 @sm:grid-cols-2 @lg:grid-cols-3">
              {staedteTop.map(({ region, points }) => (
                <div key={region}>
                  <div className="mb-1 text-[11px] text-ink/55">{region}</div>
                  <LineStat data={points} xKey="year" yKey="besucher" height={120} valueLabel={region} />
                </div>
              ))}
            </div>
          </SectionCard>

          <SectionCard
            title={intl.formatMessage({
              id: "tobis.market.venues.title",
              defaultMessage: "Tickets by venue, Germany 2025",
            })}
            colorIndex={8}
          >
            <div className="grid gap-6 @lg:grid-cols-2 @xl:grid-cols-4">
              <div>
                <div className="mb-2 text-[12px] text-ink/55">
                  {intl.formatMessage({ id: "tobis.market.byForm", defaultMessage: "By cinema form (regular, multiplex, drive-in…)" })}
                </div>
                <BarCompare
                  data={formCharts.kinoform}
                  xKey="kategorie"
                  yKey="tickets"
                  valueLabel={intl.formatMessage({ id: "tobis.market.tickets", defaultMessage: "Tickets" })}
                  height={200}
                />
                <SourceLink
                  label={intl.formatMessage({ id: "tobis.market.sources.document", defaultMessage: "Source document" })}
                  url={ffa.sources.kinoformUrl}
                />
              </div>
              <div>
                <div className="mb-2 text-[12px] text-ink/55">
                  {intl.formatMessage({ id: "tobis.market.bySize", defaultMessage: "By screen count per venue" })}
                </div>
                <BarCompare
                  data={formCharts.kinogroesse}
                  xKey="kategorie"
                  yKey="tickets"
                  valueLabel={intl.formatMessage({ id: "tobis.market.tickets", defaultMessage: "Tickets" })}
                  height={200}
                />
                <SourceLink
                  label={intl.formatMessage({ id: "tobis.market.sources.document", defaultMessage: "Source document" })}
                  url={ffa.sources.kinogroesseUrl}
                />
              </div>
              <div>
                <div className="mb-2 text-[12px] text-ink/55">
                  {intl.formatMessage({ id: "tobis.market.byPlaceSize", defaultMessage: "By town population" })}
                </div>
                <BarCompare
                  data={formCharts.ortsgroesse}
                  xKey="kategorie"
                  yKey="tickets"
                  valueLabel={intl.formatMessage({ id: "tobis.market.tickets", defaultMessage: "Tickets" })}
                  height={200}
                />
                <SourceLink
                  label={intl.formatMessage({ id: "tobis.market.sources.document", defaultMessage: "Source document" })}
                  url={ffa.sources.ortsgroesseUrl}
                />
              </div>
              <div>
                <div className="mb-2 text-[12px] text-ink/55">
                  {intl.formatMessage(
                    { id: "tobis.market.bySonderform", defaultMessage: "By special venue type, {year}" },
                    { year: ffa.sonderformenLatestYear ?? "–" },
                  )}
                </div>
                <BarCompare
                  data={ffa.sonderformen}
                  xKey="form"
                  yKey="tickets"
                  valueLabel={intl.formatMessage({ id: "tobis.market.tickets", defaultMessage: "Tickets" })}
                  height={200}
                />
              </div>
            </div>

            <div className="mt-6 text-[12px] text-ink/55">
              {intl.formatMessage({ id: "tobis.market.sonderformenOverTime", defaultMessage: "Special venue types over time" })}
            </div>
            <div className="mt-2 grid gap-4 @sm:grid-cols-2 @lg:grid-cols-3">
              {sonderformenTop.map(({ form, points }) => (
                <div key={form}>
                  <div className="mb-1 text-[11px] text-ink/55">{form}</div>
                  <LineStat data={points} xKey="year" yKey="tickets" height={120} valueLabel={form} />
                </div>
              ))}
            </div>
            <SourceLinksRow
              label={intl.formatMessage({ id: "tobis.market.sources.kinoergebnisse", defaultMessage: "Source documents:" })}
              links={ffa.sources.sonderformen.map((s) => ({ key: String(s.year), url: s.url, text: String(s.year) }))}
            />
          </SectionCard>

          <SectionCard
            title={intl.formatMessage(
              { id: "tobis.market.kpiExplorer.title", defaultMessage: "FFA annual figures ({count} metrics)" },
              { count: kpiKennzahlCount },
            )}
            colorIndex={9}
          >
            <p className="mb-3 max-w-2xl text-[12px] text-ink/55">
              {intl.formatMessage({
                id: "tobis.market.kpiExplorer.note",
                defaultMessage: "Self-extracted from the FFA source PDFs, verified against known reference values.",
              })}
            </p>
            <TobisSearch
              value={kpiSearch}
              onChange={setKpiSearch}
              label={intl.formatMessage({ id: "tobis.market.kpiExplorer.searchLabel", defaultMessage: "Search metrics" })}
              placeholder={intl.formatMessage({
                id: "tobis.market.kpiExplorer.searchPlaceholder",
                defaultMessage: "e.g. Eintrittspreis, Sitzplätze…",
              })}
            />

            {visibleKennzahlen.length === 0 ? (
              <p className="mt-3 text-sm text-ink/55">
                {intl.formatMessage({ id: "tobis.market.kpiExplorer.empty", defaultMessage: "No metric matches." })}
              </p>
            ) : (
              <div className="mt-3 max-h-52 space-y-2 overflow-y-auto">
                {visibleGroups.map(([group, kennzahlen]) => (
                  <div key={group}>
                    <div className="text-[11px] text-ink/40">{group}</div>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {kennzahlen.map((k) => {
                        const active = selectedKennzahlen.includes(k);
                        return (
                          <button
                            key={k}
                            type="button"
                            onClick={() => toggleKennzahl(k)}
                            aria-pressed={active}
                            className={
                              active
                                ? "rounded-full bg-accent px-3 py-1 text-[12px] text-on-accent"
                                : "rounded-full bg-ink/[0.06] px-3 py-1 text-[12px] text-ink/70 hover:bg-ink/10"
                            }
                          >
                            {k}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {kpiMatches.length >= 2 && (
              <div className="mt-4 flex justify-end">
                <SegmentedControl
                  value={kpiOverlay ? "overlay" : "grid"}
                  onChange={(v) => setKpiOverlay(v === "overlay")}
                  size="sm"
                  options={[
                    { value: "grid", label: intl.formatMessage({ id: "tobis.market.kpiExplorer.viewGrid", defaultMessage: "Tiles" }) },
                    { value: "overlay", label: intl.formatMessage({ id: "tobis.market.kpiExplorer.viewOverlay", defaultMessage: "Overlay (indexed)" }) },
                  ]}
                />
              </div>
            )}

            {kpiMatches.length >= 2 && kpiOverlay ? (
              kpiOverlayData.length > 0 ? (
                <div className="mt-4">
                  <MultiLineStat
                    data={kpiOverlayData}
                    xKey="year"
                    formatY={(v) => v.toFixed(0)}
                    height={280}
                    series={kpiMatches.map(({ kennzahl }) => ({ key: kennzahl, label: kennzahl }))}
                  />
                  <p className="mt-2 text-[12px] text-ink/55">
                    {intl.formatMessage(
                      { id: "tobis.market.kpiExplorer.overlayNote", defaultMessage: "Indexed to 100 in {baseYear}, the first year with a value for every selected metric." },
                      { baseYear: kpiOverlayData[0]?.year ?? "–" },
                    )}
                  </p>
                </div>
              ) : (
                <p className="mt-4 text-sm text-ink/55">
                  {intl.formatMessage({
                    id: "tobis.market.kpiExplorer.overlayEmpty",
                    defaultMessage: "No shared year with a value for every selected metric.",
                  })}
                </p>
              )
            ) : (
              kpiMatches.length > 0 && (
                <div className="mt-4 grid gap-6 @sm:grid-cols-2 @lg:grid-cols-3">
                  {kpiMatches.map(({ kennzahl, points }) => (
                    <div key={kennzahl}>
                      <div className="mb-2 text-[12px] text-ink/70">{kennzahl}</div>
                      <LineStat data={points} xKey="year" yKey="value" valueLabel={kennzahl} height={140} />
                    </div>
                  ))}
                </div>
              )
            )}
            <SourceLinksRow
              label={intl.formatMessage({ id: "tobis.market.sources.kinoergebnisse", defaultMessage: "Source documents:" })}
              links={ffa.sources.kinoergebnisse.map((s) => ({ key: String(s.year), url: s.url, text: String(s.year) }))}
            />
          </SectionCard>

          <SectionCard
            title={intl.formatMessage({ id: "tobis.market.homevideo.title", defaultMessage: "Home entertainment (aggregate)" })}
            colorIndex={10}
          >
            <p className="mb-2 max-w-2xl text-[12px] text-ink/55">
              {intl.formatMessage({
                id: "tobis.market.homevideo.note",
                defaultMessage: "Market-wide revenue, not per title, use as a ratio against a comparable cinema result.",
              })}
            </p>
            <p className="mb-2 max-w-2xl text-[12px] text-ink/40">
              {intl.formatMessage({
                id: "tobis.market.homevideo.confidenceNote",
                defaultMessage:
                  "1990–2015 read off bar-chart labels in the source PDFs (lower confidence, cross-checked against overlapping years), 2016 onward taken directly from the report text.",
              })}
            </p>
            <AreaStat
              data={ffa.homevideoMarkt}
              xKey="year"
              yKey="umsatzMioEur"
              formatY={(v) => `${compactNumber(v)}M €`}
              valueLabel={intl.formatMessage({ id: "tobis.market.revenue", defaultMessage: "Box office" })}
            />
            <SourceLinksRow
              label={intl.formatMessage({ id: "tobis.market.sources.videoergebnisse", defaultMessage: "Source documents:" })}
              links={ffa.sources.videoergebnisse.map((s) => ({ key: String(s.year), url: s.url, text: String(s.year) }))}
            />

            <div className="mt-6 text-[12px] text-ink/55">
              {intl.formatMessage({ id: "tobis.market.homevideo.segments", defaultMessage: "By purchase, rental & subscription" })}
            </div>
            <MultiLineStat
              data={homevideoSegmenteChart}
              xKey="year"
              formatY={(v) => `${compactNumber(v)}M €`}
              height={220}
              series={[
                { key: "kaufvideos", label: intl.formatMessage({ id: "tobis.market.homevideo.kaufvideos", defaultMessage: "Purchase (DVD/Blu-ray/EST)" }) },
                { key: "leihvideos_or_tvod", label: intl.formatMessage({ id: "tobis.market.homevideo.leihvideos", defaultMessage: "Rental (DVD/Blu-ray/TVoD)" }) },
                { key: "svod", label: intl.formatMessage({ id: "tobis.market.homevideo.svod", defaultMessage: "Subscription (SVoD)" }) },
              ]}
            />
            <SourceLinksRow
              label={intl.formatMessage({ id: "tobis.market.sources.videoergebnisse", defaultMessage: "Source documents:" })}
              links={ffa.sources.videoergebnisse.map((s) => ({ key: String(s.year), url: s.url, text: String(s.year) }))}
            />

            <div className="mt-6 text-[12px] text-ink/55">
              {intl.formatMessage({ id: "tobis.market.homevideo.genres", defaultMessage: "Genre mix (share of revenue, all formats)" })}
            </div>
            <p className="mb-2 max-w-2xl text-[12px] text-ink/40">
              {intl.formatMessage({
                id: "tobis.market.homevideo.genresNote",
                defaultMessage:
                  "Read off stacked bar-chart labels (chart estimate). Top 4 genres shown individually, the rest bundled as \"Other\": a full 10-genre breakdown would need a colour palette, which would break this dashboard's monochrome convention.",
              })}
            </p>
            <MultiLineStat
              data={homevideoGenresChart}
              xKey="year"
              formatY={(v) => `${v.toFixed(0)}%`}
              height={220}
              series={([...HOMEVIDEO_GENRE_TOP_CODES, "sonstige"] as const).map((code) => ({
                key: code,
                label: homevideoGenreLabel(intl, code),
              }))}
            />
            <SourceLinksRow
              label={intl.formatMessage({ id: "tobis.market.sources.videoergebnisse", defaultMessage: "Source documents:" })}
              links={ffa.sources.videoergebnisse.map((s) => ({ key: String(s.year), url: s.url, text: String(s.year) }))}
            />

            {homevideoSankeys.length > 0 && (
              <>
                <p className="mt-6 mb-2 max-w-2xl text-[12px] text-ink/40">
                  {intl.formatMessage({
                    id: "tobis.market.homevideo.sankeyNote",
                    defaultMessage:
                      "Two years shown where they differ: the most detailed one (rental still broken out by format) and the most recent one available (rental DVD/Blu-ray has largely disappeared since 2023, TVoD became its own segment instead).",
                  })}
                </p>
                <div className="mt-4 grid gap-6 @lg:grid-cols-2">
                  {homevideoSankeys.map(({ year, data }) => (
                    <div key={year}>
                      <div className="mb-2 text-[12px] text-ink/55">
                        {intl.formatMessage({ id: "tobis.market.homevideo.sankey", defaultMessage: "Revenue breakdown, {year}" }, { year })}
                      </div>
                      <SankeyStat data={data} formatY={(v) => `${compactNumber(v)}M €`} height={340} />
                      <SourceLinksRow
                        label={intl.formatMessage({ id: "tobis.market.sources.videoergebnisse", defaultMessage: "Source documents:" })}
                        links={ffa.sources.videoergebnisse
                          .filter((s) => s.year === year)
                          .map((s) => ({ key: String(s.year), url: s.url, text: String(s.year) }))}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}

            <div className="mt-6 text-[12px] text-ink/55">
              {intl.formatMessage({ id: "tobis.market.homevideo.consumers", defaultMessage: "Consumers, 2017–2025" })}
            </div>
            <div className="mt-2 grid gap-4 @sm:grid-cols-2 @lg:grid-cols-4">
              {homevideoKonsumentenByMetric.map(({ metric, points }) => (
                <div key={metric}>
                  <div className="mb-1 text-[11px] text-ink/55">{homevideoMetricLabel(intl, metric)}</div>
                  <LineStat
                    data={points}
                    xKey="year"
                    yKey="value"
                    height={120}
                    formatY={homevideoMetricFormatY(metric)}
                    valueLabel={homevideoMetricLabel(intl, metric)}
                  />
                </div>
              ))}
            </div>
            <SourceLinksRow
              label={intl.formatMessage({ id: "tobis.market.sources.videoergebnisse", defaultMessage: "Source documents:" })}
              links={ffa.sources.videoergebnisse.map((s) => ({ key: String(s.year), url: s.url, text: String(s.year) }))}
            />

            {homevideoKonsumklimaByMetric.length > 0 && (
              <>
                <div className="mt-6 text-[12px] text-ink/55">
                  {intl.formatMessage({ id: "tobis.market.konsumklima.title", defaultMessage: "Consumer climate (GfK), 2016 / 2023–2025" })}
                </div>
                <p className="mb-2 max-w-2xl text-[12px] text-ink/40">
                  {intl.formatMessage({
                    id: "tobis.market.konsumklima.note",
                    defaultMessage:
                      "Index-point change vs. prior month, as printed in the report's key-figures box. Only 4 of 15 report years print this box with numbers; the rest either don't cover the topic, show icons without a number, or only the overall index chart.",
                  })}
                </p>
                <div className="mt-2 grid gap-4 @sm:grid-cols-2 @lg:grid-cols-4">
                  {homevideoKonsumklimaByMetric.map(({ metric, points }) => (
                    <div key={metric}>
                      <div className="mb-1 text-[11px] text-ink/55">{konsumklimaMetricLabel(intl, metric)}</div>
                      <LineStat
                        data={points}
                        xKey="year"
                        yKey="value"
                        height={120}
                        formatY={formatIndexPoints}
                        valueLabel={konsumklimaMetricLabel(intl, metric)}
                      />
                    </div>
                  ))}
                </div>
                <SourceLinksRow
                  label={intl.formatMessage({ id: "tobis.market.sources.videoergebnisse", defaultMessage: "Source documents:" })}
                  links={ffa.sources.videoergebnisse
                    .filter((s) => [2016, 2023, 2024, 2025].includes(s.year))
                    .map((s) => ({ key: String(s.year), url: s.url, text: String(s.year) }))}
                />
              </>
            )}
          </SectionCard>

          <SectionCard title={intl.formatMessage({ id: "tobis.market.streaming.title", defaultMessage: "AGF — streaming reach" })} colorIndex={11}>
            <div className="mb-4 flex flex-wrap gap-1.5">
              {AGF_AUDIENCES.map((a) => {
                const active = selectedAudiences.includes(a);
                return (
                  <button
                    key={a}
                    type="button"
                    onClick={() =>
                      setSelectedAudiences((prev) =>
                        prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a],
                      )
                    }
                    aria-pressed={active}
                    className={
                      active
                        ? "rounded-full bg-accent px-3 py-1 text-[12px] text-on-accent"
                        : "rounded-full bg-ink/[0.06] px-3 py-1 text-[12px] text-ink/70 hover:bg-ink/10"
                    }
                  >
                    {a}
                  </button>
                );
              })}
            </div>

            {agfByPlatform.length === 0 ? (
              <p className="text-sm text-ink/55">
                {intl.formatMessage({ id: "tobis.market.streaming.empty", defaultMessage: "No data for this audience." })}
              </p>
            ) : (
              <div className="grid gap-6 @sm:grid-cols-2 @lg:grid-cols-4">
                {agfByPlatform.map(({ platform, points }) => (
                  <div key={platform}>
                    <div className="mb-2 text-[12px] text-ink/70">{platform}</div>
                    <MultiLineStat
                      data={points}
                      xKey="month"
                      formatX={(v) => intl.formatDate(new Date(String(v)), { month: "short", year: "2-digit" })}
                      formatY={(v) => `${v.toFixed(0)} min`}
                      height={160}
                      series={selectedAudiences.map((a) => ({ key: a, label: a }))}
                    />
                  </div>
                ))}
              </div>
            )}
            <p className="mt-4 text-[11px] text-ink/40">
              {intl.formatMessage({
                id: "tobis.market.streaming.metric",
                defaultMessage:
                  "Metric: viewing minutes per day, averaged across the whole audience group — including people who don't use the platform at all. Not the intensity of actual users.",
              })}
            </p>

            <SourceLinksRow
              label={intl.formatMessage({ id: "tobis.market.streaming.sources", defaultMessage: "Source reports:" })}
              links={agfSources.map(([url, month]) => ({
                key: url,
                url,
                text: intl.formatDate(new Date(month), { month: "short", year: "numeric" }),
              }))}
            />
          </SectionCard>

          <SectionCard title={intl.formatMessage({ id: "tobis.market.tv.title", defaultMessage: "AGF — television" })} colorIndex={12}>
            <p className="mb-4 max-w-2xl text-[12px] text-ink/55">
              {intl.formatMessage({
                id: "tobis.market.tv.note",
                defaultMessage:
                  "The source page has no archive, but every collected snapshot is kept: the daily top 10 can be switched between collected days (gaps are days without a run). Monthly market share still shows the latest month only.",
              })}
            </p>
            <div className="mb-4 flex flex-wrap gap-1.5">
              {(["total", "e1449"] as const).map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setTvAudience(a)}
                  aria-pressed={tvAudience === a}
                  className={
                    tvAudience === a
                      ? "rounded-full bg-accent px-3 py-1 text-[12px] text-on-accent"
                      : "rounded-full bg-ink/[0.06] px-3 py-1 text-[12px] text-ink/70 hover:bg-ink/10"
                  }
                >
                  {a === "total"
                    ? intl.formatMessage({ id: "tobis.market.tv.audienceTotal", defaultMessage: "All viewers" })
                    : intl.formatMessage({ id: "tobis.market.tv.audienceE1449", defaultMessage: "Viewers 14–49" })}
                </button>
              ))}
            </div>

            {tvStack.bars.length > 1 ? (
              <div className="mb-6">
                <div className="mb-2 text-[12px] text-ink/55">
                  {intl.formatMessage({
                    id: "tobis.market.tv.byDay",
                    defaultMessage: "Top-10 audience per day, split by channel",
                  })}
                </div>
                <StackedDayBars
                  data={tvStack.bars}
                  segments={tvStack.senders}
                  valueLabel={intl.formatMessage({
                    id: "tobis.market.tv.viewers",
                    defaultMessage: "Viewers (m)",
                  })}
                  formatX={(v) =>
                    intl.formatDate(new Date(String(v)), { weekday: "short", day: "numeric", month: "short" })
                  }
                />
              </div>
            ) : null}

            <div className="mb-2 flex flex-wrap items-center gap-3">
              <span className="text-[12px] text-ink/55">
                {intl.formatMessage({ id: "tobis.market.tv.dailyTop10", defaultMessage: "Daily top 10" })}
              </span>
              {tvDates.length > 1 ? (
                <Select
                  value={effectiveTvDate}
                  onChange={setTvDate}
                  options={tvDates.map((d) => ({
                    value: d,
                    label: intl.formatDate(new Date(d), { weekday: "short", day: "numeric", month: "short", year: "numeric" }),
                  }))}
                  hideEmpty
                  className="w-56"
                  id="tobis-tv-hitliste-date"
                />
              ) : (
                <span className="text-[12px] text-ink/55">{effectiveTvDate ?? "–"}</span>
              )}
            </div>
            {tvHitlisteRows.length === 0 ? (
              <p className="text-sm text-ink/55">
                {intl.formatMessage({ id: "tobis.market.tv.empty", defaultMessage: "No data yet." })}
              </p>
            ) : (
              <table className="w-full text-left text-[13px]">
                <thead>
                  <tr className="text-[12px] text-ink/45">
                    <th className="py-2 pr-4 font-normal">
                      {intl.formatMessage({ id: "tobis.market.tv.rank", defaultMessage: "#" })}
                    </th>
                    <th className="py-2 pr-4 font-normal">
                      {intl.formatMessage({ id: "tobis.market.tv.channel", defaultMessage: "Channel" })}
                    </th>
                    <th className="py-2 pr-4 font-normal">
                      {intl.formatMessage({ id: "tobis.market.tv.programme", defaultMessage: "Programme" })}
                    </th>
                    <th className="py-2 pr-4 text-right font-normal">
                      {intl.formatMessage({ id: "tobis.market.tv.viewers", defaultMessage: "Viewers (m)" })}
                    </th>
                    <th className="py-2 text-right font-normal">
                      {intl.formatMessage({ id: "tobis.market.marketShare", defaultMessage: "German market share" })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tvHitlisteRows.map((r) => (
                    <tr key={`${r.sender}-${r.startzeit}`} className="align-top text-ink/70">
                      <td className="py-2 pr-4">{r.rang}</td>
                      <td className="py-2 pr-4">{r.sender}</td>
                      <td className="py-2 pr-4">
                        {r.titel} <span className="text-ink/40">{r.startzeit}</span>
                      </td>
                      <td className="py-2 pr-4 text-right tabular-nums">
                        {r.sehbeteiligungMio != null ? r.sehbeteiligungMio.toFixed(2) : "–"}
                      </td>
                      <td className="py-2 text-right tabular-nums">{r.maPct != null ? `${r.maPct.toFixed(1)}%` : "–"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <SourceLink
              label={intl.formatMessage({ id: "tobis.market.sources.document", defaultMessage: "Source document" })}
              url={tvHitlisteRows[0]?.sourceUrl ?? null}
            />

            <div className="mt-6 mb-2 text-[12px] text-ink/55">
              {intl.formatMessage(
                { id: "tobis.market.tv.monthlyMarketShare", defaultMessage: "Monthly market share, {period}" },
                { period: tvMarktanteilRows[0]?.period ?? "–" },
              )}
            </div>
            <BarCompare
              data={tvMarktanteilChart}
              xKey="sender"
              yKey="anteilPct"
              formatY={(v) => `${v.toFixed(1)}%`}
              valueLabel={intl.formatMessage({ id: "tobis.market.marketShare", defaultMessage: "German market share" })}
              height={280}
              // Dieselben Senderfarben wie im Tagesdiagramm darueber: zwei
              // Farbsprachen fuer dieselben Sender auf einer Seite waeren
              // schlechter als gar keine.
              colorByCategory
            />
            <SourceLink
              label={intl.formatMessage({ id: "tobis.market.sources.document", defaultMessage: "Source document" })}
              url={tvMarktanteilRows[0]?.sourceUrl ?? null}
            />
          </SectionCard>
        </>
      )}
    </TobisPageShell>
  );
}
